/**
 * Copyright (c) 2019-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql, type TypedDocumentNode } from "@apollo/client";
import { useSuspenseQuery } from "@apollo/client/react";
import { Heading, Text, Image, Skeleton } from "@ndla/primitives";
import { SafeLink } from "@ndla/safelink";
import { styled } from "@ndla/styled-system/jsx";
import { Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FILM_ID } from "../../constants";
import type { GQLAllMoviesQuery, GQLAllMoviesQueryVariables } from "../../graphqlTypes";
import { movieResourceTypes } from "./resourceTypes";

const LetterHeading = styled(Heading, {
  base: {
    borderBottom: "1px solid",
    borderColor: "stroke.subtle",
  },
});

const MovieTextWrapper = styled("div", {
  base: { flex: "1" },
});

const MovieImage = styled(Image, {
  base: {
    width: "surface.3xsmall",
  },
});

const StyledSafeLink = styled(SafeLink, {
  base: {
    display: "flex",
    gap: "small",
    minHeight: "surface.4xsmall",
    overflow: "hidden",
    "& [data-title]": {
      textDecoration: "underline",
    },
    _hover: {
      "& [data-title]": {
        textDecoration: "none",
      },
    },
  },
});

const MovieGroup = styled("section", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "medium",
  },
});

const LETTER_REGEXP = /[A-Z\WÆØÅ]+/;

type MovieType = NonNullable<GQLAllMoviesQuery["searchWithoutPagination"]>["results"][0];

const groupMovies = (movies: MovieType[]) => {
  const sortedMovies: Exclude<MovieType, { __typename: "NodeSearchResult" | undefined }>[] = movies
    .filter((movie) => movie.__typename !== "NodeSearchResult")
    .toSorted((a, b) => a.title.localeCompare(b.title, "nb"));

  const grouped = sortedMovies.reduce<Record<string, MovieType[]>>((acc, movie) => {
    const firstChar = movie.title[0]?.toUpperCase() ?? "";
    const isLetter = firstChar?.match(LETTER_REGEXP);
    const char = isLetter ? firstChar : "#";
    if (acc[char]) {
      acc[char]!.push(movie);
    } else {
      acc[char] = [movie];
    }
    return acc;
  }, {});

  return Object.entries(grouped).map(([letter, movies]) => ({
    letter,
    movies,
  }));
};

const LoadingShimmer = () => {
  return (
    <div>
      {["#", "A", "B", "C"].map((letter, idx) => {
        return (
          <MovieGroup key={`Loading-${idx}`}>
            <LetterHeading textStyle="title.medium" fontWeight="bold" asChild consumeCss>
              <h2>{letter}</h2>
            </LetterHeading>
            {Array.from({ length: 4 }).map((_, idx2) => {
              return (
                <StyledSafeLink to="" disabled={true} key={idx2}>
                  <Skeleton css={{ width: "surface.3xsmall", minWidth: "surface.3xsmall", height: "[75px]" }} />
                  <MovieTextWrapper>
                    <Skeleton css={{ marginBottom: "xxsmall", width: "surface.xsmall" }}>
                      <Heading textStyle="title.small" asChild consumeCss data-title="">
                        <h3>&nbsp;</h3>
                      </Heading>
                    </Skeleton>
                    <Skeleton css={{ width: "surface.medium" }}>
                      <Text textStyle="body.small">&nbsp;</Text>
                      <Text textStyle="body.small">&nbsp;</Text>
                    </Skeleton>
                  </MovieTextWrapper>
                </StyledSafeLink>
              );
            })}
          </MovieGroup>
        );
      })}
    </div>
  );
};

export const AllMoviesAlphabetically = () => (
  <Suspense fallback={<LoadingShimmer />}>
    <AllMoviesAlphabeticallyContent />
  </Suspense>
);

const AllMoviesAlphabeticallyContent = () => {
  const { t, i18n } = useTranslation();
  const allMovies = useSuspenseQuery(allMoviesQuery, {
    variables: {
      resourceTypes: movieResourceTypes.map((resourceType) => resourceType.id).join(","),
      language: i18n.language,
    },
  });

  const movies = allMovies.data?.searchWithoutPagination?.results;
  const groupedMovies = useMemo(() => {
    if (!movies) return [];
    return groupMovies(movies);
  }, [movies]);

  return (
    <>
      {groupedMovies.map(({ letter, movies }) => (
        <MovieGroup key={letter}>
          <LetterHeading
            textStyle="title.medium"
            fontWeight="bold"
            aria-label={t("filmfrontpage.allMovieGroupTitleLabel", { letter })}
            asChild
            consumeCss
          >
            <h2>{letter}</h2>
          </LetterHeading>
          {movies.map((movie) => {
            const context = movie.contexts.find((c) => c.rootId === FILM_ID);
            // TODO: Variants
            return (
              <StyledSafeLink to={context?.url ?? ""} key={movie.id}>
                {(movie.__typename === "ArticleSearchResult" || movie.__typename === "LearningpathSearchResult") &&
                  !!movie.metaImage?.url && (
                    <MovieImage alt="" loading="lazy" sizes={"100px"} src={movie.metaImage.url} />
                  )}
                <MovieTextWrapper>
                  <Heading textStyle="title.small" asChild consumeCss data-title="">
                    <h3>{movie.title}</h3>
                  </Heading>
                  <Text textStyle="body.small">{movie.metaDescription}</Text>
                </MovieTextWrapper>
              </StyledSafeLink>
            );
          })}
        </MovieGroup>
      ))}
    </>
  );
};

const allMoviesQuery: TypedDocumentNode<GQLAllMoviesQuery, GQLAllMoviesQueryVariables> = gql`
  query allMovies($resourceTypes: String!, $language: String!) {
    searchWithoutPagination(
      resourceTypes: $resourceTypes
      language: $language
      fallback: "true"
      subjects: "urn:subject:20"
      contextTypes: "standard"
      sort: "title"
    ) {
      results {
        id
        metaDescription
        ... on ArticleSearchResult {
          metaImage {
            url
          }
        }
        ... on LearningpathSearchResult {
          metaImage {
            url
          }
        }
        title
        contexts {
          contextId
          url
          rootId
        }
      }
    }
  }
`;
